import { LOCALE_ID, Injector, APP_INITIALIZER, EnvironmentProviders, Provider, importProvidersFrom } from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';

import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { registerLocaleData } from '@angular/common';

import { LocaleService } from '@app/core/ngx-translate/local.service';
import { AppConfigService } from 'app/core/config/app-config.service';

// Import locale data
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import localeHu from '@angular/common/locales/hu';
import localeSv from '@angular/common/locales/sv';
import localeCs from '@angular/common/locales/cs';
import localeSk from '@angular/common/locales/sk';
import localeRu from '@angular/common/locales/ru';
import localeJa from '@angular/common/locales/ja';
import localeTr from '@angular/common/locales/tr';
import localeZh from '@angular/common/locales/zh';
import localeRo from '@angular/common/locales/ro';
import localeNb from '@angular/common/locales/nb';
import localeHe from '@angular/common/locales/he';

// Register locales once globally
registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeEn);
registerLocaleData(localeFr);
registerLocaleData(localeHu);
registerLocaleData(localeSv);
registerLocaleData(localeCs);
registerLocaleData(localeSk);
registerLocaleData(localeRu);
registerLocaleData(localeJa);
registerLocaleData(localeTr);
registerLocaleData(localeZh);
registerLocaleData(localeRo);
registerLocaleData(localeNb);
registerLocaleData(localeHe);

/**
 * Factory function to load translations from assets
 */
export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n-studema/', '.json');
}

/**
 * Function to get the locale based on NGX-Translate's current language
 */
export function getLocaleId(translate: TranslateService, appConfig?: AppConfigService): string {
  const lang = translate.currentLang || appConfig?.get('defaultLanguage', 'en') || 'en';
  switch (lang) {
    case 'de':
      return 'de-DE';
    case 'es':
      return 'es-ES';
    case 'fr':
      return 'fr-FR';
    default:
      return 'en-US';
  }
}

/**
 * Function to initialize translation service before the app starts
 */

export function initializeTranslation(injector: Injector): () => Promise<void> {
  return () => {
    const translateService = injector.get(TranslateService);
    const localeService = injector.get(LocaleService);

    // Initialize language from localStorage
    localeService.initializeLanguage();

    return new Promise<void>((resolve) => {
      translateService.onLangChange.subscribe((event) => {
        console.log('Language switched:', event.lang);
        resolve();
      });
    });
  };
}

/**
 * Provides the ngx-translate configuration for Angular's provider system
 */
export const provideNgxTranslate = (): Array<Provider | EnvironmentProviders> => [
  importProvidersFrom(
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
    })
  ),
  {
    provide: APP_INITIALIZER,
    useFactory: initializeTranslation,
    deps: [Injector],
    multi: true,
  },
  {
    provide: LOCALE_ID,
    useFactory: (localeService: LocaleService) => localeService.getLocaleFromLanguage(localStorage.getItem('user_language') || 'en'),
    deps: [LocaleService],
  },
];
